import { SocialSubmissionTypes, SubmissionChannels } from '@enums'
import {
  AnalyticsData,
  AnalyticsPerspective,
  AnalyticsSourceMap,
  MetricEntry,
  MetricKey,
  MetricSource,
} from './types'

const overviewMetricKeys: Record<AnalyticsPerspective, MetricKey[]> = {
  'pre-purchase': [
    'followers',
    'avg_engagement_pct',
    'avg_impressions_pct',
    'avg_reach_pct',
    'avg_emv',
  ],
  'post-purchase': ['followers', 'engagement_pct', 'impressions_pct', 'reach_pct', 'emv'],
}

const overviewBrandConnectMetricKeys: Record<AnalyticsPerspective, MetricKey[]> = {
  'pre-purchase': [
    // BRAND-9137: Overview should show Followers and Average Engagement Rate
    'followers',
    'avg_engagement_pct',
  ],
  'post-purchase': [
    // BRAND-9139: Overview shows Followers and Engagement Rate only
    'followers',
    'engagement_pct',
  ],
}

// BRAND-9137: New Viewer Performance section for YouTube BrandConnect (pre-purchase)
// BRAND-9139: Add Viewer Performance section for post-purchase
const viewerPerformanceBrandConnectMetricKeys: Record<AnalyticsPerspective, MetricKey[]> = {
  'pre-purchase': [
    'total_video_count',
    'total_reach',
    'total_impressions',
    'total_engaged_views',
    'total_likes',
    'total_comments',
  ],
  'post-purchase': [
    'reach',
    'impressions',
    'engaged_views',
    'watch_time_hours',
    'avg_view_duration_secs',
    'avg_view_percentage',
  ],
}

const engagementBrandConnectMetricKeys: Record<AnalyticsPerspective, MetricKey[]> = {
  // BRAND-9137: Use Engagement Breakdown for pre-purchase with avg_* metrics
  // BRAND-9139: Reorder pre-purchase engagement metrics
  'pre-purchase': [
    'avg_reach',
    'avg_impressions',
    'avg_engaged_views',
    'avg_likes',
    'avg_comments',
    'avg_videos_added_playlist',
  ],
  // BRAND-9139: Update post-purchase engagement metrics
  'post-purchase': ['likes', 'comments', 'shares', 'playlist_adds'],
}

const performanceMetricKeys: Record<AnalyticsPerspective, MetricKey[]> = {
  'pre-purchase': ['est_cpe', 'est_cpv', 'est_cpm', 'est_emv_multiplier'],
  'post-purchase': ['cpe', 'cpv', 'cpm', 'emv_multiplier'],
}

const engagementMetricKeys: Record<AnalyticsPerspective, MetricKey[]> = {
  'pre-purchase': [
    'avg_likes',
    'avg_comments',
    'avg_shares',
    'avg_engagement',
    'avg_impressions',
    'avg_reach',
    'avg_engaged_views',
    'avg_videos_added_playlist',
  ],
  // facebook_views: data-gated, only returned by API for Reels (BRAND-9209)
  'post-purchase': ['likes', 'comments', 'shares', 'engagement', 'impressions', 'facebook_views', 'reach'],
}

const percentageBasedMetricKeys: MetricKey[] = [
  'avg_engagement_pct',
  'avg_impressions_pct',
  'avg_reach_pct',
  'engagement_pct',
  'impressions_pct',
  'reach_pct',
]

const currencyBasedMetricKeys: MetricKey[] = [
  'est_cpe',
  'est_cpm',
  'est_cpv',
  'cpe',
  'cpm',
  'cpv',
  'avg_emv',
  'emv',
]

const tiktokSourceMap: AnalyticsSourceMap = {
  'pre-purchase': {
    followers: 'tiktok',
    avg_engagement: 'tiktok_tribe',
    avg_engagement_pct: 'tiktok_tribe',
    avg_impressions: 'tiktok_tribe',
    avg_impressions_pct: 'tiktok_tribe',
    avg_reach: 'tiktok_tribe',
    avg_reach_pct: 'tiktok_tribe',
    est_cpe: 'tiktok_tribe',
    est_cpm: 'tiktok_tribe',
    est_cpv: 'tiktok_tribe',
    avg_emv: 'tiktok_tribe',
    est_emv_multiplier: 'tiktok_tribe',
    avg_likes: 'tiktok_tribe',
    avg_comments: 'tiktok_tribe',
    avg_shares: 'tiktok_tribe',
  },
  'post-purchase': {
    followers: 'tiktok',
    engagement: 'tiktok',
    engagement_pct: 'tiktok',
    impressions: 'tiktok',
    impressions_pct: 'tiktok',
    reach: 'tiktok',
    reach_pct: 'tiktok',
    cpe: 'tiktok_tribe',
    cpm: 'tiktok_tribe',
    cpv: 'tiktok_tribe',
    emv: 'tiktok_tribe',
    emv_multiplier: 'tiktok_tribe',
    likes: 'tiktok',
    comments: 'tiktok',
    shares: 'tiktok',
  },
}

const xSourceMap: AnalyticsSourceMap = {
  'pre-purchase': {
    followers: 'x',
    avg_engagement: 'x_tribe',
    avg_engagement_pct: 'x_tribe',
    avg_impressions: 'x_tribe',
    avg_impressions_pct: 'x_tribe',
    avg_reach: 'x_tribe',
    avg_reach_pct: 'x_tribe',
    est_cpe: 'x_tribe',
    est_cpm: 'x_tribe',
    est_cpv: 'x_tribe',
    avg_emv: 'x_tribe',
    est_emv_multiplier: 'x_tribe',
    avg_likes: 'x_tribe',
    avg_comments: 'x_tribe',
    avg_shares: 'x_tribe',
  },
  'post-purchase': {
    followers: 'x',
    engagement: 'x',
    engagement_pct: 'x',
    impressions: 'x',
    impressions_pct: 'x',
    reach: 'x',
    reach_pct: 'x',
    cpe: 'x_tribe',
    cpm: 'x_tribe',
    cpv: 'x_tribe',
    emv: 'x_tribe',
    emv_multiplier: 'x_tribe',
    likes: 'x',
    comments: 'x',
    shares: 'x',
  },
}

const metaSourcemap: AnalyticsSourceMap = {
  'pre-purchase': {
    followers: 'meta',
    avg_engagement: 'meta_tribe',
    avg_engagement_pct: 'meta_tribe',
    avg_impressions: 'meta_tribe',
    avg_impressions_pct: 'meta_tribe',
    avg_reach: 'meta_tribe',
    avg_reach_pct: 'meta_tribe',
    est_cpe: 'meta_tribe',
    est_cpm: 'meta_tribe',
    est_cpv: 'meta_tribe',
    avg_emv: 'meta_tribe',
    est_emv_multiplier: 'meta_tribe',
    avg_likes: 'meta_tribe',
    avg_comments: 'meta_tribe',
    avg_shares: 'meta_tribe',
  },
  'post-purchase': {
    followers: 'meta',
    engagement: 'meta',
    engagement_pct: 'meta',
    impressions: 'meta',
    impressions_pct: 'meta',
    reach: 'meta',
    reach_pct: 'meta',
    cpe: 'meta_tribe',
    cpm: 'meta_tribe',
    cpv: 'meta_tribe',
    emv: 'meta_tribe',
    emv_multiplier: 'meta_tribe',
    likes: 'meta',
    comments: 'meta',
    shares: 'meta',
    facebook_views: 'meta',
  },
}

const snapchatSourceMap: AnalyticsSourceMap = {
  'pre-purchase': {
    followers: 'snapchat',
    avg_engagement: 'snapchat_tribe',
    avg_engagement_pct: 'snapchat_tribe',
    avg_impressions: 'snapchat_tribe',
    avg_impressions_pct: 'snapchat_tribe',
    avg_reach: 'snapchat_tribe',
    avg_reach_pct: 'snapchat_tribe',
    est_cpe: 'snapchat_tribe',
    est_cpm: 'snapchat_tribe',
    est_cpv: 'snapchat_tribe',
    avg_emv: 'snapchat_tribe',
    est_emv_multiplier: 'snapchat_tribe',
    avg_likes: 'snapchat_tribe',
    avg_comments: 'snapchat_tribe',
    avg_shares: 'snapchat_tribe',
  },
  'post-purchase': {
    followers: 'snapchat',
    engagement: 'snapchat',
    engagement_pct: 'snapchat',
    impressions: 'snapchat',
    impressions_pct: 'snapchat',
    reach: 'snapchat',
    reach_pct: 'snapchat',
    cpe: 'snapchat_tribe',
    cpm: 'snapchat_tribe',
    cpv: 'snapchat_tribe',
    emv: 'snapchat_tribe',
    emv_multiplier: 'snapchat_tribe',
    likes: 'snapchat',
    comments: 'snapchat',
    shares: 'snapchat',
  },
}

const youtubeSourceMap: AnalyticsSourceMap = {
  'pre-purchase': {
    followers: 'youtube',
    avg_engagement: 'youtube_tribe',
    avg_engagement_pct: 'youtube_tribe',
    avg_impressions: 'youtube_tribe',
    avg_impressions_pct: 'youtube_tribe',
    avg_reach: 'youtube_tribe',
    avg_reach_pct: 'youtube_tribe',
    est_cpe: 'youtube_tribe',
    est_cpm: 'youtube_tribe',
    est_cpv: 'youtube_tribe',
    avg_emv: 'youtube_tribe',
    est_emv_multiplier: 'youtube_tribe',
    avg_likes: 'youtube_tribe',
    avg_comments: 'youtube_tribe',
    avg_shares: 'youtube_tribe',
    avg_engaged_views: 'youtube_tribe',
    avg_videos_added_playlist: 'youtube_tribe',
    total_impressions: 'youtube_tribe',
    total_engaged_views: 'youtube_tribe',
    total_likes: 'youtube_tribe',
    total_comments: 'youtube_tribe',
    total_reach: 'youtube_tribe',
    total_video_count: 'youtube_tribe',
  },
  'post-purchase': {
    followers: 'youtube',
    engagement: 'youtube',
    engagement_pct: 'youtube',
    impressions: 'youtube',
    impressions_pct: 'youtube',
    reach: 'youtube',
    reach_pct: 'youtube',
    cpe: 'youtube_tribe',
    cpm: 'youtube_tribe',
    cpv: 'youtube_tribe',
    emv: 'youtube_tribe',
    emv_multiplier: 'youtube_tribe',
    likes: 'youtube',
    comments: 'youtube',
    shares: 'youtube',
    engaged_views: 'youtube',
    watch_time_hours: 'youtube',
    playlist_adds: 'youtube',
  },
}

const youtubeBrandConnectSourceMap: AnalyticsSourceMap = {
  'pre-purchase': {
    followers: 'youtube_brand_connect',
    avg_engagement: 'youtube_brand_connect',
    avg_engagement_pct: 'youtube_brand_connect',
    avg_impressions: 'youtube_brand_connect',
    avg_impressions_pct: 'youtube_brand_connect',
    avg_reach: 'youtube_brand_connect',
    avg_reach_pct: 'youtube_brand_connect',
    est_cpe: 'youtube_brand_connect',
    est_cpm: 'youtube_brand_connect',
    est_cpv: 'youtube_brand_connect',
    avg_emv: 'youtube_brand_connect',
    est_emv_multiplier: 'youtube_brand_connect',
    avg_likes: 'youtube_brand_connect',
    avg_comments: 'youtube_brand_connect',
    avg_shares: 'youtube_brand_connect',
    avg_engaged_views: 'youtube_brand_connect',
    avg_videos_added_playlist: 'youtube_brand_connect',
    total_impressions: 'youtube_brand_connect',
    total_engaged_views: 'youtube_brand_connect',
    total_likes: 'youtube_brand_connect',
    total_comments: 'youtube_brand_connect',
    total_reach: 'youtube_brand_connect',
    total_video_count: 'youtube_brand_connect',
  },
  'post-purchase': {
    followers: 'youtube_brand_connect',
    engagement: 'youtube_brand_connect',
    engagement_pct: 'youtube_brand_connect',
    impressions: 'youtube_brand_connect',
    impressions_pct: 'youtube_brand_connect',
    reach: 'youtube_brand_connect',
    reach_pct: 'youtube_brand_connect',
    cpe: 'youtube_brand_connect',
    cpm: 'youtube_brand_connect',
    cpv: 'youtube_brand_connect',
    emv: 'youtube_brand_connect',
    emv_multiplier: 'youtube_brand_connect',
    likes: 'youtube_brand_connect',
    comments: 'youtube_brand_connect',
    shares: 'youtube_brand_connect',
    engaged_views: 'youtube_brand_connect',
    watch_time_hours: 'youtube_brand_connect',
    playlist_adds: 'youtube_brand_connect',
    avg_view_duration_secs: 'youtube_brand_connect',
    avg_view_percentage: 'youtube_brand_connect',
  },
}

const getSource = (
  metricKey: MetricKey,
  perspective: AnalyticsPerspective,
  socialSubmissionType: SocialSubmissionTypes,
  isProxy = false,
  channel: SubmissionChannels | undefined,
  isYouTubeBrandConnect = false,
): MetricSource | null => {
  if (isProxy) {
    return 'manual'
  }

  if (socialSubmissionType === SocialSubmissionTypes.TikTok) {
    return tiktokSourceMap[perspective][metricKey] ?? null
  }

  if (channel === SubmissionChannels.X) {
    return xSourceMap[perspective][metricKey] ?? null
  }

  if (channel === SubmissionChannels.Snapchat) {
    return snapchatSourceMap[perspective][metricKey] ?? null
  }

  if (channel === SubmissionChannels.YouTube) {
    if (isYouTubeBrandConnect) {
      return youtubeBrandConnectSourceMap[perspective][metricKey] ?? null
    }
    return youtubeSourceMap[perspective][metricKey] ?? null
  }

  if (
    [
      SocialSubmissionTypes.Standard,
      SocialSubmissionTypes.Reel,
      SocialSubmissionTypes.Carousel,
      SocialSubmissionTypes.Story,
    ].includes(socialSubmissionType)
  ) {
    return metaSourcemap[perspective][metricKey] ?? null
  }

  return null
}

const getMetricEntries = (
  metricKeys: MetricKey[],
  analyticsData: AnalyticsData,
  perspective: AnalyticsPerspective,
  socialSubmissionType: SocialSubmissionTypes,
  isProxy = false,
  channel: SubmissionChannels | undefined,
  isYouTubeBrandConnect = false,
) => {
  const entries: MetricEntry[] = []
  metricKeys.forEach((metric) => {
    if (analyticsData[metric] === 'hidden') {
      return
    }
    // Skip metrics that were deleted (not in the object)
    if (!(metric in analyticsData)) {
      return
    }
    if (!analyticsData[metric]) {
      entries.push({
        metric,
        source: getSource(
          metric,
          perspective,
          socialSubmissionType,
          isProxy,
          channel,
          isYouTubeBrandConnect,
        ),
        value: null,
      })
      return
    }
    const entry: MetricEntry = {
      type: 'number',
      metric,
      source: getSource(
        metric,
        perspective,
        socialSubmissionType,
        isProxy,
        channel,
        isYouTubeBrandConnect,
      ),
      value: analyticsData[metric]!,
    }

    if (percentageBasedMetricKeys.includes(metric)) {
      entry.type = 'percentage'
    } else if (currencyBasedMetricKeys.includes(metric)) {
      entry.type = 'currency'
    }

    entries.push(entry)
  })

  return entries
}

export const getOverviewMetricEntries = (
  analyticsData: AnalyticsData,
  socialSubmissionType: SocialSubmissionTypes,
  perspective: AnalyticsPerspective,
  isProxy = false,
  channel: SubmissionChannels | undefined = undefined,
): MetricEntry[] => {
  return getMetricEntries(
    overviewMetricKeys[perspective],
    analyticsData,
    perspective,
    socialSubmissionType,
    isProxy,
    channel,
  )
}

export const getPerformanceMetricEntries = (
  analyticsData: AnalyticsData,
  socialSubmissionType: SocialSubmissionTypes,
  perspective: AnalyticsPerspective,
  isProxy = false,
  channel: SubmissionChannels | undefined = undefined,
): MetricEntry[] => {
  return getMetricEntries(
    performanceMetricKeys[perspective],
    analyticsData,
    perspective,
    socialSubmissionType,
    isProxy,
    channel,
  )
}

export const getEngagementMetricEntries = (
  analyticsData: AnalyticsData,
  socialSubmissionType: SocialSubmissionTypes,
  perspective: AnalyticsPerspective,
  isProxy = false,
  channel: SubmissionChannels | undefined = undefined,
): MetricEntry[] => {
  return getMetricEntries(
    engagementMetricKeys[perspective],
    analyticsData,
    perspective,
    socialSubmissionType,
    isProxy,
    channel,
  )
}

export const getOverviewBrandConnectMetrics = (
  analyticsData: AnalyticsData,
  perspective: AnalyticsPerspective,
  socialSubmissionType: SocialSubmissionTypes,
): MetricEntry[] => {
  const metricKeys = overviewBrandConnectMetricKeys[perspective]
  const defaultAnalytics = metricKeys.reduce((acc, key) => {
    acc[key] = null
    return acc
  }, {} as AnalyticsData)
  const isYouTubeBrandConnect = true
  return getMetricEntries(
    metricKeys,
    { ...defaultAnalytics, ...analyticsData },
    perspective,
    socialSubmissionType,
    false,
    SubmissionChannels.YouTube,
    isYouTubeBrandConnect,
  )
}

export const getViewerPerformanceBrandConnectMetrics = (
  analyticsData: AnalyticsData,
  perspective: AnalyticsPerspective,
  socialSubmissionType: SocialSubmissionTypes,
): MetricEntry[] => {
  const metricKeys = viewerPerformanceBrandConnectMetricKeys[perspective]
  const defaultAnalytics = metricKeys.reduce((acc, key) => {
    acc[key] = null
    return acc
  }, {} as AnalyticsData)
  const isYouTubeBrandConnect = true
  return getMetricEntries(
    metricKeys,
    { ...defaultAnalytics, ...analyticsData },
    perspective,
    socialSubmissionType,
    false,
    SubmissionChannels.YouTube,
    isYouTubeBrandConnect,
  )
}

export const getEngagementBrandConnectMetrics = (
  analyticsData: AnalyticsData,
  perspective: AnalyticsPerspective,
  socialSubmissionType: SocialSubmissionTypes,
): MetricEntry[] => {
  const metricKeys = engagementBrandConnectMetricKeys[perspective]
  const defaultAnalytics = metricKeys.reduce((acc, key) => {
    acc[key] = null
    return acc
  }, {} as AnalyticsData)
  const isYouTubeBrandConnect = true
  return getMetricEntries(
    metricKeys,
    { ...defaultAnalytics, ...analyticsData },
    perspective,
    socialSubmissionType,
    false,
    SubmissionChannels.YouTube,
    isYouTubeBrandConnect,
  )
}

export const FINANCIAL_ANALYTICS_KEYS: MetricKey[] = [
  // Pre Purchase
  'est_cpe',
  'est_cpv',
  'est_cpm',
  'est_emv_multiplier',
  'avg_emv',
  // Post Purchase
  'cpe',
  'cpv',
  'cpm',
  'emv_multiplier',
  'emv',
]
